import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSystemUsageTracker } from '../hooks/useSystemUsageTracker';
import { ChevronDown, ChevronUp, Maximize2, Star, X } from 'lucide-react';
import { ToolPageShell } from '../components/tool';
import { useAuth } from '../hooks/useAuth';
import { safeInvoke, streamInvoke } from '../lib/safeInvoke';
import { supabase } from '../lib/supabase';
import { TextSkeleton } from '../components/shared/TextSkeleton';
import { ReadingFeedback } from '../components/shared/ReadingFeedback';
import { ReadingActions } from '../components/shared/ReadingActions';
import { ReadingBasis } from '../components/shared/ReadingBasis';
import { EmptyState } from '../components/ui/EmptyState';
import { useI18n } from '../hooks/useI18n';
import {
  calculatePlanetPositions,
  calculateAscMC,
  longitudeToSign,
  longitudeToHouse,
  solarHouseCusp,
  PLANET_NAMES_PT,
  PLANET_GLYPHS,
} from '../services/ephemeris';
import type { PlanetPosition as EphPlanetPosition } from '../services/ephemeris';

// ─── Types ────────────────────────────────────────────────────────────
interface PlanetPosition {
  name: string;
  symbol: string;
  sign: string;
  degree: number;
  longitude: number;
  house: number;
}
interface Aspect {
  planet1: string; // English key: sun, moon, mercury, venus, mars, jupiter, saturn, uranus, neptune, pluto
  planet2: string;
  aspect: string;  // conjunction | trine | square | opposition | sextile
  orb: number;
  quality: string; // harmonious | tense | neutral
}
interface ChartData {
  planets: PlanetPosition[];
  houses: { number: number; sign: string; degree: number }[];
  aspects: Aspect[];
}

// ─── DB reconstruction helpers ───────────────────────────────────────
const ZODIAC_SIGNS_ORDER = [
  'Áries','Touro','Gêmeos','Câncer','Leão','Virgem',
  'Libra','Escorpião','Sagitário','Capricórnio','Aquário','Peixes',
];
const PT_NAME_MAP: Record<string, string> = {
  sun:'Sol', moon:'Lua', mercury:'Mercúrio', venus:'Vênus', mars:'Marte',
  jupiter:'Júpiter', saturn:'Saturno', uranus:'Urano', neptune:'Netuno', pluto:'Plutão',
  ascendant:'ASC', mc:'MC',
};
const SYMBOL_MAP_DB: Record<string, string> = {
  sun:'☉', moon:'☽', mercury:'☿', venus:'♀', mars:'♂',
  jupiter:'♃', saturn:'♄', uranus:'♅', neptune:'♆', pluto:'♇',
  ascendant:'AC', mc:'MC',
};
function dbToChartData(
  planetMap: Record<string, { sign: string; degree: number; house: number }>,
  _houseMap: Record<string, { sign: string; cusp_degree: number }>,
  aspects:   Aspect[],
): ChartData {
  // Derive solar house cusp from stored Sun position
  const sunData    = planetMap['sun'];
  const sunSignIdx = sunData ? ZODIAC_SIGNS_ORDER.indexOf(sunData.sign) : 0;
  const sunLon     = sunSignIdx >= 0 ? sunSignIdx * 30 + (sunData?.degree ?? 0) : 0;
  const cusp0      = solarHouseCusp(sunLon);

  const planets: PlanetPosition[] = Object.entries(planetMap).filter(([key]) => key !== 'ascendant').map(([key, val]) => {
    const signIdx = ZODIAC_SIGNS_ORDER.indexOf(val.sign);
    const lon     = signIdx >= 0 ? signIdx * 30 + val.degree : 0;
    return {
      name:      PT_NAME_MAP[key]   ?? key,
      symbol:    SYMBOL_MAP_DB[key] ?? key.slice(0, 2).toUpperCase(),
      sign:      val.sign,
      degree:    val.degree,
      longitude: parseFloat(lon.toFixed(4)),
      house:     longitudeToHouse(lon, cusp0),
    };
  });

  // Rebuild houses from solar cusp
  const houses = Array.from({ length: 12 }, (_, i) => {
    const cuspLon  = ((cusp0 + i * 30) % 360 + 360) % 360;
    const cuspInfo = longitudeToSign(cuspLon);
    return { number: i + 1, sign: cuspInfo.sign, degree: cuspInfo.degree };
  });

  return { planets, houses, aspects };
}

// ─── Zodiac metadata ─────────────────────────────────────────────────
const SIGN_SYMBOLS = ['♈','♉','♊','♋','♌','♍','♎','♏','♐','♑','♒','♓'];
const ELEMENT_COLORS: Record<string, string> = {
  fire: '#F5C842', earth: '#86a876', air: '#7ecee3', water: '#9b7fd4',
};
const SIGN_ELEMENTS = ['fire','earth','air','water','fire','earth','air','water','fire','earth','air','water'];

const PLANET_COLORS: Record<string, string> = {
  'Sol': '#F5C842', 'Lua': '#c8d9e6', 'Mercúrio': '#a8c4a2', 'Vênus': '#e8a0bf',
  'Marte': '#e87070', 'Júpiter': '#d4a017', 'Saturno': '#9a8a6a',
  'Urano': '#7ecee3', 'Netuno': '#7b9fe0', 'Plutão': '#b07ec4',
  'MC': '#F5C842',
};

// English key → Portuguese planet name (matches function response)
const EN_TO_PT: Record<string, string> = {
  sun: 'Sol', moon: 'Lua', mercury: 'Mercúrio', venus: 'Vênus', mars: 'Marte',
  jupiter: 'Júpiter', saturn: 'Saturno', uranus: 'Urano', neptune: 'Netuno', pluto: 'Plutão',
};
const ASPECT_COLORS: Record<string, string> = {
  conjunction: '#F5C842',
  trine:       '#4ade80',
  square:      '#f87171',
  opposition:  '#f87171',
  sextile:     '#60a5fa',
};

// ─── Client-side calc helpers ─────────────────────────────────────────

const ASPECT_DEFS_UI = [
  { name: 'conjunction', angle: 0,   orb: 8, quality: 'neutral'    },
  { name: 'sextile',     angle: 60,  orb: 6, quality: 'harmonious' },
  { name: 'square',      angle: 90,  orb: 8, quality: 'tense'      },
  { name: 'trine',       angle: 120, orb: 8, quality: 'harmonious' },
  { name: 'opposition',  angle: 180, orb: 8, quality: 'tense'      },
];
const UI_NAME_TO_EN: Record<string, string> = {
  'Sol':'sun','Lua':'moon','Mercúrio':'mercury','Vênus':'venus','Marte':'mars',
  'Júpiter':'jupiter','Saturno':'saturn','Urano':'uranus','Netuno':'neptune','Plutão':'pluto',
};
function calcAspectsFromUI(planets: PlanetPosition[]): Aspect[] {
  const personal = planets.filter(p => !['ASC', 'MC'].includes(p.name));
  const aspects: Aspect[] = [];
  for (let i = 0; i < personal.length - 1; i++) {
    for (let j = i + 1; j < personal.length; j++) {
      const diff  = Math.abs(personal[i].longitude - personal[j].longitude);
      const angle = diff > 180 ? 360 - diff : diff;
      for (const def of ASPECT_DEFS_UI) {
        const orb = Math.abs(angle - def.angle);
        if (orb <= def.orb) {
          aspects.push({
            planet1: UI_NAME_TO_EN[personal[i].name] ?? personal[i].name.toLowerCase(),
            planet2: UI_NAME_TO_EN[personal[j].name] ?? personal[j].name.toLowerCase(),
            aspect:  def.name,
            orb:     parseFloat(orb.toFixed(2)),
            quality: def.quality,
          });
          break;
        }
      }
    }
  }
  return aspects;
}

async function geocodeCity(city: string): Promise<{ lat: number; lng: number } | null> {
  try {
    const res  = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(city)}&format=json&limit=1`,
      { headers: { 'Accept-Language': 'pt-BR' } },
    );
    const data = await res.json() as Array<{ lat: string; lon: string }>;
    if (!data.length) return null;
    return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch {
    return null;
  }
}

function ephemerisToUI(
  epPositions: EphPlanetPosition[],
  solarCusp0:  number,
  mcLon:       number | null,
): PlanetPosition[] {
  const result: PlanetPosition[] = epPositions.map(p => ({
    name:      PLANET_NAMES_PT[p.planet],
    symbol:    PLANET_GLYPHS[p.planet],
    sign:      p.sign,
    degree:    p.degree,
    longitude: p.longitude,
    house:     p.house, // already solar-based from calculatePlanetPositions
  }));
  if (mcLon !== null) {
    const mcInfo  = longitudeToSign(mcLon);
    const mcHouse = longitudeToHouse(mcLon, solarCusp0);
    result.push({ name: 'MC', symbol: 'MC', sign: mcInfo.sign, degree: mcInfo.degree, longitude: mcLon, house: mcHouse });
  }
  return result;
}

const PT_TO_EN_KEY: Record<string, string> = {
  'Sol':'sun','Lua':'moon','Mercúrio':'mercury','Vênus':'venus','Marte':'mars',
  'Júpiter':'jupiter','Saturno':'saturn','Urano':'uranus','Netuno':'neptune','Plutão':'pluto',
  'MC':'mc',
};

async function storeChart(
  planets:       PlanetPosition[],
  houses:        ChartData['houses'],
  aspects:       Aspect[],
  birthDate:     string,
  birthTime:     string,
  birthCity:     string,
  isSolar:       boolean,
  ascendantSign: string | null = null,
  ascendantDeg:  number | null = null,
): Promise<void> {
  const planetsJsonb: Record<string, object> = {};
  for (const p of planets) {
    const key = PT_TO_EN_KEY[p.name] ?? p.name.toLowerCase();
    planetsJsonb[key] = { sign: p.sign, degree: p.degree, house: p.house, retrograde: false };
  }
  const housesJsonb: Record<string, object> = {};
  for (const h of houses) {
    const planetsInHouse = planets
      .filter(p => p.house === h.number && !['ASC', 'MC'].includes(p.name))
      .map(p => PT_TO_EN_KEY[p.name] ?? p.name.toLowerCase());
    housesJsonb[String(h.number)] = { sign: h.sign, cusp_degree: h.degree, planets: planetsInHouse };
  }
  await safeInvoke<Record<string, unknown>>('birth-chart', {
    body: {
      clientChart: {
        planetsJsonb,
        housesJsonb,
        aspects,
        birthDate,
        birthTime,
        birthCity,
        isSolar,
        ascendantSign: ascendantSign ?? undefined,
        ascendantDegree: ascendantDeg ?? undefined,
        calculationMethod: 'astronomy-engine',
      },
    },
    fallback: {},
    timeout: 15_000,
  });
}

// ─── Natal Wheel SVG ─────────────────────────────────────────────────
function NatalWheel({
  planets, aspects, onPlanetClick, solarCusp0 = 0, ascLon: _ascLon = null, maxWidth = '480px',
}: {
  planets: PlanetPosition[]; aspects: Aspect[]; onPlanetClick: (p: PlanetPosition) => void;
  solarCusp0?: number; ascLon?: number | null; maxWidth?: string;
}) {
  const cx = 200, cy = 200, r = 180;
  const houseR = 140, zodiacR = 165, planetR = 115, innerR = 70;

  // angleDeg=0 → 12 o'clock, angleDeg=90 → 9 o'clock (counter-clockwise)
  function polarToCart(angleDeg: number, radius: number) {
    const rad = ((-angleDeg - 90) * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
  }

  // Standard western chart orientation: House 1 (ASC) at LEFT (9 o'clock).
  // anchorLon = solarCusp0 ensures toWheelAngle(solarCusp0) = 90° = LEFT.
  // This also places H4 at bottom, H7 at right, H10 at top, all four angular
  // cusps sit exactly on the cardinal axes, giving a horizontal ASC-DSC line.
  const anchorLon = solarCusp0;

  // Convert any ecliptic longitude to a wheel angle.
  function toWheelAngle(longitude: number) {
    return (longitude - anchorLon + 90 + 360) % 360;
  }

  // House 1 cusp longitude in the solar house system (start of Sun's sign).
  // This is the ground truth for where house divisions belong, NOT anchorLon,
  // which only controls which direction points to 12 o'clock (MC).
  const houseCusp0 = solarCusp0;

  // Stagger planet radii so clustered planets (within 12°) don't overlap.
  const CLUSTER_DEG = 12;
  const planetRadii = new Map<string, number>(
    planets.map(p => [p.name, planetR]),
  );
  const sorted = [...planets].sort((a, b) => toWheelAngle(a.longitude) - toWheelAngle(b.longitude));
  for (let i = 0; i < sorted.length; i++) {
    const a = toWheelAngle(sorted[i].longitude);
    for (let j = i + 1; j < sorted.length; j++) {
      const b = toWheelAngle(sorted[j].longitude);
      const gap = Math.min(Math.abs(b - a), 360 - Math.abs(b - a));
      if (gap > CLUSTER_DEG) break;
      // Alternate inner / outer for each member past the first
      const offset = (j - i) % 2 === 1 ? -22 : 22;
      planetRadii.set(sorted[j].name, planetR + offset);
    }
  }

  return (
    <svg viewBox="0 0 400 400" style={{ width: '100%', maxWidth, margin: '0 auto', display: 'block' }} aria-label="Mapa Astral">
      {/* Outer background */}
      <circle cx={cx} cy={cy} r={r} fill="#0F0620" stroke="rgba(212,175,55,0.5)" strokeWidth="1.2" />

      {/* Zodiac ring, 12 signs × 30°, placed at absolute ecliptic longitudes */}
      {SIGN_SYMBOLS.map((sym, i) => {
        const startAngle = toWheelAngle(i * 30);
        const midAngle   = toWheelAngle(i * 30 + 15);
        const color      = ELEMENT_COLORS[SIGN_ELEMENTS[i]];
        const p1 = polarToCart(startAngle, zodiacR);
        const p2 = polarToCart(startAngle, r - 2);
        const pMid = polarToCart(midAngle, (zodiacR + r - 2) / 2);
        return (
          <g key={i}>
            <line x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} stroke="rgba(212,175,55,0.35)" strokeWidth="0.8" />
            <text x={pMid.x} y={pMid.y} textAnchor="middle" dominantBaseline="middle"
              fontSize="11" fill={color} fontFamily="serif">
              {sym}
            </text>
          </g>
        );
      })}

      {/* Outer zodiac circle */}
      <circle cx={cx} cy={cy} r={zodiacR} fill="none" stroke="rgba(212,175,55,0.5)" strokeWidth="1" />

      {/* House divisions: cusps at actual solar-house boundaries (houseCusp0 + i×30°) */}
      {Array.from({ length: 12 }, (_, i) => {
        const angle  = toWheelAngle(houseCusp0 + i * 30);
        const p1     = polarToCart(angle, innerR);
        const p2     = polarToCart(angle, zodiacR);
        const isAngle = i % 3 === 0;
        return (
          <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke={isAngle ? '#D4AF37' : 'rgba(212,175,55,0.3)'}
            strokeWidth={isAngle ? '1.4' : '0.7'} />
        );
      })}

      {/* House numbers at midpoints of actual house sectors */}
      {Array.from({ length: 12 }, (_, i) => {
        const midAngle = toWheelAngle(houseCusp0 + i * 30 + 15);
        const p = polarToCart(midAngle, (innerR + houseR) / 2);
        return (
          <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="middle"
            fontSize="9" fill="rgba(200,220,235,0.65)" fontFamily="sans-serif">
            {i + 1}
          </text>
        );
      })}

      {/* Inner circle */}
      <circle cx={cx} cy={cy} r={innerR} fill="#0A0416" stroke="rgba(212,175,55,0.5)" strokeWidth="1" />

      {/* Aspect lines, drawn inside innerR so they don't overlap planets */}
      {aspects.map((asp, i) => {
        const pt1 = planets.find(p => p.name === EN_TO_PT[asp.planet1]);
        const pt2 = planets.find(p => p.name === EN_TO_PT[asp.planet2]);
        if (!pt1 || !pt2) return null;
        const p1 = polarToCart(toWheelAngle(pt1.longitude), innerR - 4);
        const p2 = polarToCart(toWheelAngle(pt2.longitude), innerR - 4);
        const color   = ASPECT_COLORS[asp.aspect] ?? 'var(--border-subtle)';
        const opacity = asp.quality === 'harmonious' ? 0.55 : asp.quality === 'tense' ? 0.65 : 0.35;
        return (
          <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
            stroke={color} strokeWidth="0.8" opacity={opacity} />
        );
      })}

      {/* Center symbol */}
      <text x={cx} y={cy} textAnchor="middle" dominantBaseline="middle"
        fontSize="16" fill="var(--petrol)" fontFamily="serif" opacity="0.6">✦</text>

      {/* Planets, drawn at staggered radii to avoid overlap within clusters */}
      {planets.map((planet) => {
        const angle  = toWheelAngle(planet.longitude);
        const pRadius = planetRadii.get(planet.name) ?? planetR;
        const pos    = polarToCart(angle, pRadius);
        const color  = PLANET_COLORS[planet.name] ?? 'var(--text-secondary)';
        return (
          <g key={planet.name} style={{ cursor: 'pointer' }} onClick={() => onPlanetClick(planet)} role="button" tabIndex={0}>
            <circle cx={pos.x} cy={pos.y} r="10" fill="var(--bg-elevated)" stroke={color} strokeWidth="1.5" />
            <text x={pos.x} y={pos.y} textAnchor="middle" dominantBaseline="middle"
              fontSize="8" fill={color} fontFamily="sans-serif" fontWeight="600">
              {planet.symbol ?? planet.name.slice(0, 2)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ─── Aspect grid (triangular matrix) ─────────────────────────────────
const ASPECT_SYMBOLS: Record<string, string> = {
  conjunction: '☌',
  sextile:     '⚹',
  square:      '□',
  trine:       '△',
  opposition:  '☍',
};

function AspectGrid({ planets, aspects }: { planets: PlanetPosition[]; aspects: Aspect[] }) {
  // Traditional order, only include the 10 classical planets that exist in the chart
  const order = ['Sol','Lua','Mercúrio','Vênus','Marte','Júpiter','Saturno','Urano','Netuno','Plutão'];
  const list  = order.filter(n => planets.some(p => p.name === n));

  function getAspect(a: string, b: string): Aspect | null {
    const aEn = Object.keys(EN_TO_PT).find(k => EN_TO_PT[k] === a);
    const bEn = Object.keys(EN_TO_PT).find(k => EN_TO_PT[k] === b);
    if (!aEn || !bEn) return null;
    return aspects.find(x =>
      (x.planet1 === aEn && x.planet2 === bEn) ||
      (x.planet1 === bEn && x.planet2 === aEn),
    ) ?? null;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', fontSize: '0.75rem', margin: '0 auto' }}>
        <tbody>
          {list.map((rowName, i) => (
            <tr key={rowName}>
              {/* Row header */}
              <td style={{
                padding: '0.4rem 0.6rem', textAlign: 'right',
                color: PLANET_COLORS[rowName] ?? 'var(--text-secondary)',
                fontWeight: 600, whiteSpace: 'nowrap', borderRight: '1px solid var(--border-subtle)',
              }}>
                {rowName}
              </td>
              {/* Aspect cells, triangular: only show cells left of diagonal */}
              {list.slice(0, i).map(colName => {
                const asp = getAspect(rowName, colName);
                const color = asp ? (ASPECT_COLORS[asp.aspect] ?? 'var(--text-tertiary)') : 'transparent';
                return (
                  <td key={colName} title={asp ? `${rowName} ${asp.aspect} ${colName} (orb ${asp.orb.toFixed(1)}°)` : ''}
                    style={{
                      width: '32px', height: '32px', textAlign: 'center',
                      border: '1px solid var(--border-subtle)',
                      background: asp ? 'rgba(255,255,255,0.02)' : 'transparent',
                    }}>
                    {asp && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1 }}>
                        <span style={{ fontSize: '0.9rem', color }}>{ASPECT_SYMBOLS[asp.aspect] ?? '·'}</span>
                        <span style={{ fontSize: '0.55rem', color: 'var(--text-muted)' }}>{asp.orb.toFixed(1)}°</span>
                      </div>
                    )}
                  </td>
                );
              })}
              {/* Self-cell on diagonal */}
              <td style={{
                width: '32px', height: '32px', background: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)', textAlign: 'center',
                color: PLANET_COLORS[rowName] ?? 'var(--text-muted)', fontSize: '0.9rem',
              }}>
                {planets.find(p => p.name === rowName)?.symbol ?? ''}
              </td>
            </tr>
          ))}
          {/* Column headers at bottom */}
          <tr>
            <td />
            {list.slice(0, list.length - 1).map(colName => (
              <td key={colName} style={{
                padding: '0.4rem 0.2rem', textAlign: 'center',
                color: PLANET_COLORS[colName] ?? 'var(--text-secondary)',
                fontSize: '0.85rem',
              }}>
                {planets.find(p => p.name === colName)?.symbol ?? colName.slice(0, 2)}
              </td>
            ))}
            <td />
          </tr>
        </tbody>
      </table>
      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', justifyContent: 'center', marginTop: '1rem', fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
        {Object.entries(ASPECT_SYMBOLS).map(([name, sym]) => (
          <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ color: ASPECT_COLORS[name], fontSize: '0.9rem' }}>{sym}</span>
            <span style={{ textTransform: 'capitalize' }}>{name === 'conjunction' ? 'Conjunção' : name === 'sextile' ? 'Sextil' : name === 'square' ? 'Quadratura' : name === 'trine' ? 'Trígono' : 'Oposição'}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Zoomed chart modal ───────────────────────────────────────────────
function ZoomedChartModal({
  planets, aspects, solarCusp0, ascLon, onClose, onPlanetClick,
}: {
  planets: PlanetPosition[]; aspects: Aspect[]; solarCusp0: number; ascLon?: number | null;
  onClose: () => void; onPlanetClick: (p: PlanetPosition) => void;
}) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(10,13,26,0.94)', backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        overflow: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '1200px', margin: '2rem auto', padding: '1.25rem',
          background: 'var(--bg-base)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-lg)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="font-display" style={{ fontSize: '1.125rem', color: 'var(--petrol)', margin: 0 }}>
            ✦ Mapa Astral, Visão do Astrólogo
          </h2>
          <button
            className="btn btn-ghost btn-icon"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Large wheel */}
        <div style={{ padding: '1rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)', marginBottom: '1.25rem' }}>
          <NatalWheel
            planets={planets}
            aspects={aspects}
            onPlanetClick={onPlanetClick}
            solarCusp0={solarCusp0}
            ascLon={ascLon}
            maxWidth="820px"
          />
        </div>

        {/* Aspect grid */}
        <div style={{ padding: '1rem', background: 'var(--bg-surface)', borderRadius: 'var(--radius-md)' }}>
          <h3 className="font-display" style={{ fontSize: '0.95rem', color: 'var(--text-primary)', marginBottom: '0.75rem', textAlign: 'center' }}>
            Quadro de Aspectos
          </h3>
          <AspectGrid planets={planets} aspects={aspects} />
        </div>
      </div>
    </div>
  );
}

// ─── Planet table row ─────────────────────────────────────────────────
function PlanetRow({ p, onClick }: { p: PlanetPosition; onClick: () => void }) {
  const color = PLANET_COLORS[p.name] ?? 'var(--text-secondary)';
  const deg = Math.round(p.degree);
  return (
    <tr style={{ cursor: 'pointer' }} onClick={onClick}
      onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'rgba(31,78,95,0.05)')}
      onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}>
      <td style={{ padding: '0.625rem 1rem', color, fontWeight: 600, fontSize: 'var(--text-label)' }}>{p.name}</td>
      <td style={{ padding: '0.625rem 1rem', color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>{p.sign}</td>
      <td style={{ padding: '0.625rem 1rem', color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)' }}>{deg}°</td>
      <td style={{ padding: '0.625rem 1rem', color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)' }}>Casa {p.house}</td>
    </tr>
  );
}

// ─── Bottom sheet ─────────────────────────────────────────────────────
function PlanetSheet({ planet, onClose }: { planet: PlanetPosition; onClose: () => void }) {
  const color = PLANET_COLORS[planet.name] ?? 'var(--text-secondary)';
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200 }} onClick={onClose}>
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} />
      <div
        style={{ position: 'absolute', bottom: 0, left: 0, right: 0, backgroundColor: 'var(--bg-elevated)',
          borderTop: '1px solid var(--border-default)', borderRadius: '1.25rem 1.25rem 0 0',
          padding: '1.5rem', maxHeight: '70vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}
        className="animate-fade-in"
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.75rem', color }}>{planet.symbol ?? '●'}</span>
            <div>
              <h3 className="font-display" style={{ fontSize: '1.125rem', color: 'var(--text-primary)', margin: 0 }}>{planet.name}</h3>
              <p style={{ color: 'var(--text-tertiary)', fontSize: 'var(--text-caption)', margin: '0.1rem 0 0' }}>
                {planet.sign} · {Math.round(planet.degree)}° · Casa {planet.house}
              </p>
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Fechar"><X size={20} /></button>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', lineHeight: 1.7 }}>
          {planet.name} em {planet.sign} na Casa {planet.house} indica uma forte orientação para as questões
          regidas por essa casa, com a energia e os temas do signo influenciando sua expressão. Este posicionamento
          revela aspectos importantes da sua jornada astrológica pessoal.
        </p>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────
const TOP_PLANETS = ['Sol','Lua','Mercúrio','Vênus','Marte'];

const INTERP_MSGS = [
  'Analisando seus planetas e casas...',
  'Interpretando aspectos do seu mapa...',
  'Consultando influências do Ascendente...',
  'Preparando sua leitura personalizada...',
  'Sintetizando os temas centrais do seu mapa...',
];

function useRotatingMessage(messages: string[], interval = 3500, active = true): string {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setIdx(i => (i + 1) % messages.length), interval);
    return () => clearInterval(id);
  }, [active, interval, messages.length]);
  return messages[idx];
}

export function BirthChartPage() {
  useSystemUsageTracker('mapa_natal');
  const { profile } = useAuth();
  const navigate    = useNavigate();
  const { t, lang } = useI18n();
  const isAdmin = profile?.role === 'admin';
  const [chart,          setChart]          = useState<ChartData | null>(null);
  const [ascendantSign,  setAscendantSign]  = useState<string | null>(null);
  const [ascendantDeg,   setAscendantDeg]   = useState<number | null>(null);
  const [ascLon,         setAscLon]         = useState<number | null>(null);
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState<string | null>(null);
  const [interpretation, setInterpretation] = useState('');
  const [streamingInterp,setStreamingInterp]= useState('');
  const [interpLoading,  setInterpLoading]  = useState(false);
  const [interpError,    setInterpError]    = useState<string | null>(null);
  const interpMsg = useRotatingMessage(INTERP_MSGS, 3500, interpLoading);
  const [selectedPlanet,setSelectedPlanet]  = useState<PlanetPosition | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [showAll,       setShowAll]         = useState(false);
  const freshComputed = useRef(false); // true when chart was just computed client-side

  // Load chart positions: DB first, then edge function if missing
  useEffect(() => {
    if (!profile?.id || !profile?.birth_date) return;
    loadChartData();
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadChartData() {
    setLoading(true);
    setError(null);

    // Fast path: use accurate DB data if already computed with astronomy-engine
    const { data: dbChart } = await supabase
      .from('natal_charts')
      .select('planets, houses, aspects, calculation_method')
      .eq('user_id', profile!.id)
      .is('invalidated_at', null)
      .order('calculated_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (!isAdmin && dbChart?.planets && (dbChart as Record<string, unknown>).calculation_method === 'astronomy-engine') {
      const planetMap = dbChart.planets as Record<string, { sign: string; degree: number; house: number }>;
      const chartData = dbToChartData(
        planetMap,
        (dbChart.houses ?? {}) as Record<string, { sign: string; cusp_degree: number }>,
        (dbChart.aspects ?? []) as Aspect[],
      );
      const dbAsc = planetMap['ascendant'];
      if (dbAsc) {
        const ascSignIdx = ZODIAC_SIGNS_ORDER.indexOf(dbAsc.sign);
        if (ascSignIdx >= 0) {
          setAscLon(ascSignIdx * 30 + dbAsc.degree);
          setAscendantSign(dbAsc.sign);
          setAscendantDeg(dbAsc.degree);
        }
      }
      setChart(chartData);
      setLoading(false);
      return;
    }

    // Slow path: compute client-side using astronomy-engine (NASA/JPL accuracy)
    try {
      const birthDate = profile!.birth_date!;
      const birthTime = profile!.birth_time ?? '12:00';
      const birthCity = profile!.birth_city ?? null;

      const [y, mo, d] = birthDate.split('-').map(Number);
      const [h, m]     = birthTime.split(':').map(Number);
      const birthDt    = new Date(Date.UTC(y, mo - 1, d, h, m));

      let mcLon: number | null = null;
      let clientAscSign: string | null = null;
      let clientAscDeg: number | null = null;
      if (birthCity) {
        const geo = await geocodeCity(birthCity);
        if (geo) {
          const ascMc = calculateAscMC(birthDt, geo.lat, geo.lng);
          mcLon = ascMc.mc;
          const ascInfo = longitudeToSign(ascMc.asc);
          clientAscSign = ascInfo.sign;
          clientAscDeg  = ascInfo.degree;
          setAscLon(ascMc.asc);
        }
      }

      // Solar house system: House 1 = Sun's sign
      const epPositions = calculatePlanetPositions(birthDt);
      const sunPos      = epPositions.find(p => p.planet === 'Sun')!;
      const solarCusp0  = solarHouseCusp(sunPos.longitude);
      const isSolar     = !birthCity;

      const uiPlanets = ephemerisToUI(epPositions, solarCusp0, mcLon);

      const houses = Array.from({ length: 12 }, (_, i) => {
        const cuspLon  = ((solarCusp0 + i * 30) % 360 + 360) % 360;
        const cuspInfo = longitudeToSign(cuspLon);
        return { number: i + 1, sign: cuspInfo.sign, degree: cuspInfo.degree };
      });

      const aspects = calcAspectsFromUI(uiPlanets);
      // Store first so birth-chart-interp can find the chart when interpretation starts
      await storeChart(uiPlanets, houses, aspects, birthDate, birthTime, birthCity ?? '', isSolar, clientAscSign, clientAscDeg);
      freshComputed.current = true; // signal interpretation to bypass cache
      setAscendantSign(clientAscSign);
      setAscendantDeg(clientAscDeg);
      setChart({ planets: uiPlanets, houses, aspects });

    } catch (clientErr) {
      console.warn('[birth-chart] client-side calc failed, falling back to edge function:', clientErr);
      const { data, fromFallback, error: rawError } = await safeInvoke<ChartData>('birth-chart', {
        fallback: { planets: [], houses: [], aspects: [] },
        timeout:  30_000,
      });
      setChart(data);
      if (fromFallback) {
        const lower = (rawError ?? '').toLowerCase();
        if (lower.includes('limite') || lower.includes('rate')) {
          setError('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
        } else if (lower.includes('nascimento') || lower.includes('birth_date')) {
          setError(t('pages.birthChart.emptySubtitle'));
        } else if (lower.includes('missing env') || lower.includes('api_key')) {
          setError('Serviço temporariamente indisponível. Tente novamente em alguns minutos.');
        } else {
          setError(t('pages.birthChart.starsMoving'));
        }
      }
    }
    setLoading(false);
  }

  // Stream interpretation once chart positions are ready
  useEffect(() => {
    if (!chart?.planets.length) return;
    const force = freshComputed.current || isAdmin;
    freshComputed.current = false;
    startInterpretation(force);
  }, [chart?.planets.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-run interpretation when language changes so text matches selected language
  useEffect(() => {
    if (!chart?.planets.length) return;
    startInterpretation(true);
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  function startInterpretation(force = false) {
    setInterpLoading(true);
    setStreamingInterp('');
    setInterpError(null);

    const firstName = profile?.full_name?.split(' ')[0] ?? '';
    function fixName(text: string) {
      if (!firstName) return text;
      return text.replace(/\bNativo\b/g, firstName);
    }

    streamInvoke('birth-chart-interp', {
      body:    force ? { force: true } : {},
      timeout: 120_000,
      onChunk(accumulated)  { setStreamingInterp(fixName(accumulated)); },
      onComplete(fullText) {
        setInterpretation(fixName(fullText ?? ''));
        setStreamingInterp('');
        setInterpLoading(false);
      },
      onError() {
        setInterpLoading(false);
        setInterpError(t('pages.birthChart.starsMoving'));
      },
    });
  }

  function generate() {
    setChart(null);
    setInterpretation('');
    setError(null);
    loadChartData();
  }

  if (!profile?.birth_date) {
    return (
      <ToolPageShell icon={Star} title={t('pages.birthChart.title')}>
        <EmptyState
          emoji="🌠"
          title={t('pages.birthChart.emptyTitle')}
          subtitle={t('pages.birthChart.emptySubtitle')}
          cta={t('pages.birthChart.emptyCta')}
          ctaAction={() => navigate('/profile')}
        />
      </ToolPageShell>
    );
  }

  const visiblePlanets = chart ? (showAll ? chart.planets : chart.planets.filter(p => TOP_PLANETS.includes(p.name))) : [];
  const firstName = profile.full_name?.split(' ')[0];

  return (
    <ToolPageShell
      icon={Star}
      title={t('pages.birthChart.title')}
      subtitle={firstName ? `${firstName} · ${t('pages.birthChart.houseSystem')}` : t('pages.birthChart.houseSystem')}
    >

      {loading && (
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <TextSkeleton lines={4} message={t('pages.birthChart.calculating')} />
        </div>
      )}

      {error && !loading && (
        <div style={{ padding: '1rem 1.25rem', backgroundColor: 'rgba(245,200,66,0.06)', border: '1px solid rgba(245,200,66,0.2)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)' }}>{error}</span>
          <button className="btn btn-ghost btn-sm" onClick={generate} style={{ whiteSpace: 'nowrap' }}>
            {t('pages.birthChart.retry')}
          </button>
        </div>
      )}

      {chart && chart.planets.length > 0 && !loading && (
        <>
          {/* Natal Wheel */}
          <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem 1rem', position: 'relative' }}>
            <button
              onClick={() => setZoomed(true)}
              className="btn btn-ghost btn-icon"
              aria-label="Ampliar mapa astral"
              title="Ampliar mapa astral e ver quadro de aspectos"
              style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', zIndex: 2 }}
            >
              <Maximize2 size={18} />
            </button>
            <NatalWheel
              planets={chart.planets}
              aspects={chart.aspects ?? []}
              onPlanetClick={setSelectedPlanet}
              solarCusp0={(() => {
                const sun = chart.planets.find(p => p.name === 'Sol');
                return sun ? Math.floor(sun.longitude / 30) * 30 : 0;
              })()}
              ascLon={ascLon}
            />
          </div>

          {/* Planet table */}
          <div className="card" style={{ marginBottom: '1.5rem', overflow: 'hidden', padding: 0 }}>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border-subtle)' }}>
              <h2 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', margin: 0 }}>{t('pages.birthChart.planetPositions')}</h2>
            </div>
            <div className="overflow-table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('pages.birthChart.planet')}</th><th>{t('pages.birthChart.sign')}</th><th>{t('pages.birthChart.degree')}</th><th>{t('pages.birthChart.house')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visiblePlanets.map(p => (
                    <PlanetRow key={p.name} p={p} onClick={() => setSelectedPlanet(p)} />
                  ))}
                </tbody>
              </table>
            </div>
            {chart.planets.length > TOP_PLANETS.length && (
              <button
                className="btn btn-ghost btn-block"
                style={{ borderTop: '1px solid var(--border-subtle)', borderRadius: 0, minHeight: '48px' }}
                onClick={() => setShowAll(v => !v)}
              >
                {showAll
                  ? <><ChevronUp size={16} /> {t('pages.birthChart.showLess')}</>
                  : <><ChevronDown size={16} /> {t('pages.birthChart.showAll').replace('{n}', String(chart.planets.length - TOP_PLANETS.length))}</>}
              </button>
            )}
          </div>

          {/* Interpretation, streams progressively */}
          <div className="card" style={{ marginBottom: '1.5rem' }}>
            <h2 className="font-display" style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem' }}>
              {t('pages.birthChart.interpretation')} ✦
            </h2>

            {interpLoading && streamingInterp ? (
              /* Streaming: plain render to avoid per-chunk React re-render */
              <div
                className="reading-text"
                style={{ whiteSpace: 'pre-wrap', fontSize: 'var(--text-label)', lineHeight: 1.75, opacity: 0.92 }}
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{ __html: streamingInterp.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
              />
            ) : interpLoading ? (
              <TextSkeleton lines={6} message={interpMsg} />
            ) : interpError && !interpretation ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                <p style={{ color: 'var(--text-tertiary)', fontStyle: 'italic', margin: 0 }}>{interpError}</p>
                <button className="btn btn-ghost btn-sm" onClick={() => startInterpretation(true)}>
                  {t('pages.birthChart.retry')}
                </button>
              </div>
            ) : interpretation ? (
              <>
                <div className="reading-text">
                  {interpretation.split('\n').filter(Boolean).map((para, i) => (
                    <p key={i}
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                    />
                  ))}
                </div>
                <ReadingActions pageName="Mapa Astral" reading={interpretation} signEmoji="🌠" userName={profile.full_name?.split(' ')[0] ?? undefined} />
                <ReadingFeedback feature="birth-chart" />

                {/* Cross-sell: Astro Coach */}
                <div style={{ border: '1px solid rgba(245,200,66,0.3)', background: 'rgba(245,200,66,0.06)', borderRadius: 'var(--radius-sm)', padding: '1.25rem', marginTop: '1.5rem', textAlign: 'center' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-small)', marginBottom: '0.5rem' }}>
                    Seu mapa natal revelou quem você é. O <strong>Astro Coach</strong> usa esse mapa para guiar sua jornada semana a semana, 12 semanas de autoconhecimento profundo.
                  </p>
                  <Link to="/astro-coach" style={{ color: '#F5C842', fontWeight: 600, fontSize: 'var(--text-small)', textDecoration: 'none' }}>
                    Começar o Astro Coach →
                  </Link>
                </div>

                <ReadingBasis
                  sunSign={chart.planets.find(p => p.name === 'Sol')?.sign}
                  sunDegree={chart.planets.find(p => p.name === 'Sol')?.degree}
                  moonSign={chart.planets.find(p => p.name === 'Lua')?.sign}
                  ascendantSign={ascendantSign ?? undefined}
                  ascendantDegree={ascendantDeg ?? undefined}
                />
              </>
            ) : null}
          </div>
        </>
      )}

      {/* Retry prompt when chart is empty and no error message set */}
      {!loading && (!chart || chart.planets.length === 0) && !error && (
        <div style={{ textAlign: 'center', padding: '2rem 0' }}>
          <button className="btn btn-primary" onClick={generate}>
            {t('pages.birthChart.calculateCta')}
          </button>
        </div>
      )}

      {/* Planet detail bottom sheet */}
      {selectedPlanet && <PlanetSheet planet={selectedPlanet} onClose={() => setSelectedPlanet(null)} />}

      {zoomed && chart && (
        <ZoomedChartModal
          planets={chart.planets}
          aspects={chart.aspects ?? []}
          solarCusp0={(() => {
            const sun = chart.planets.find(p => p.name === 'Sol');
            return sun ? Math.floor(sun.longitude / 30) * 30 : 0;
          })()}
          ascLon={ascLon}
          onClose={() => setZoomed(false)}
          onPlanetClick={setSelectedPlanet}
        />
      )}


    </ToolPageShell>
  );
}
